/**
 * Content of the www.bookeper.pl/mcp install page (BOO-5261 U5, spec §B.11).
 *
 * The server's URL, tools, parameters and disclaimer are never typed here: they
 * come from mcp-tools.generated.json, the `tools/list` answer of the real server
 * recorded by apps/backend/scripts/generate_public_mcp_discovery.py (drift
 * guard: apps/backend/tests/unit/api/public/mcp/test_public_mcp_discovery.py).
 * Every config snippet below is built from that URL.
 */
import snapshot from './mcp-tools.generated.json';

export interface McpToolParameter {
  name: string;
  required: boolean;
}

export interface McpTool {
  name: string;
  title: string;
  description: string;
  readOnly: boolean;
  parameters: McpToolParameter[];
}

export interface McpSnapshot {
  registryName: string;
  serverName: string;
  version: string;
  url: string;
  transport: string;
  disclaimer: string;
  tools: McpTool[];
}

export const MCP_SNAPSHOT: McpSnapshot = snapshot;

export const MCP_PAGE_PATH = '/mcp';
export const MCP_PAGE_URL = `https://www.bookeper.pl${MCP_PAGE_PATH}`;
export const PUBLIC_OPENAPI_URL = 'https://www.bookeper.pl/api/public/mcp/openapi.json';

/** The name every client config registers the server under. */
const CLIENT_SERVER_NAME = 'bookeper';

const asJson = (value: unknown) => JSON.stringify(value, null, 2);

export interface InstallClient {
  id: string;
  name: string;
  /** Polish steps, one paragraph each. */
  steps: string[];
  /** The text to paste, or null when the client only takes the URL in a form. */
  snippet: string | null;
  /** Where the snippet goes (a file path or "terminal"), shown above it. */
  snippetLabel?: string;
}

export function installClients(url: string = MCP_SNAPSHOT.url): InstallClient[] {
  return [
    {
      id: 'claude',
      name: 'Claude (claude.ai i aplikacja desktopowa)',
      steps: [
        'Otwórz Ustawienia → Konektory i wybierz „Dodaj własny konektor”. Wpisz nazwę, np. Bookeper, i wklej adres serwera. Uwierzytelnianie nie jest potrzebne.',
        'W nowej rozmowie włącz konektor Bookeper w menu narzędzi. Dostępność własnych konektorów zależy od planu Claude.',
      ],
      snippet: url,
      snippetLabel: 'Adres serwera',
    },
    {
      id: 'claude-code',
      name: 'Claude Code',
      steps: ['Dodaj serwer jednym poleceniem w terminalu:'],
      snippet: `claude mcp add --transport http ${CLIENT_SERVER_NAME} ${url}`,
      snippetLabel: 'Terminal',
    },
    {
      id: 'chatgpt',
      name: 'ChatGPT',
      steps: [
        'Włącz tryb dewelopera (Ustawienia → Aplikacje i konektory → Zaawansowane), a następnie utwórz nowy konektor.',
        'Wklej adres serwera i wybierz brak uwierzytelniania. Dostępność trybu dewelopera zależy od planu ChatGPT.',
      ],
      snippet: url,
      snippetLabel: 'Adres serwera',
    },
    {
      id: 'cursor',
      name: 'Cursor',
      steps: [
        'Dodaj serwer do pliku konfiguracji MCP: globalnie w ~/.cursor/mcp.json albo w projekcie w .cursor/mcp.json.',
      ],
      snippet: asJson({ mcpServers: { [CLIENT_SERVER_NAME]: { url } } }),
      snippetLabel: '.cursor/mcp.json',
    },
    {
      id: 'vscode',
      name: 'VS Code (GitHub Copilot)',
      steps: [
        'Dodaj serwer do pliku .vscode/mcp.json w projekcie (albo poleceniem „MCP: Add Server” w palecie poleceń).',
      ],
      snippet: asJson({ servers: { [CLIENT_SERVER_NAME]: { type: 'http', url } } }),
      snippetLabel: '.vscode/mcp.json',
    },
    {
      id: 'inspector',
      name: 'MCP Inspector (test bez asystenta)',
      steps: [
        'Uruchom Inspector, wybierz transport „Streamable HTTP”, wklej adres serwera i połącz się. Zobaczysz listę narzędzi i możesz wywołać każde z nich.',
      ],
      snippet: 'npx @modelcontextprotocol/inspector',
      snippetLabel: 'Terminal',
    },
  ];
}
